import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { getSocket } from '@/lib/socket';

// --- Types ---

export interface CopyProvider {
  id: string;
  source: 'VANTAGE' | 'BOT';
  external_id: string | null;
  external_strategy_no: string | null;
  display_name: string;
  description: string | null;
  avatar_url: string | null;
  symbol: string;
  is_verified: boolean;
  is_active: boolean;
  is_visible: boolean;
  provider_equity: number | null;
  total_trades: number;
  winning_trades: number;
  losing_trades: number;
  total_pnl: number;
  win_rate: number;
  strategy_metadata: Record<string, any> | null;
  created_at: string;
}

export interface ProviderTrade {
  id: string;
  external_position_id: string;
  symbol: string;
  type: 'BUY' | 'SELL';
  volume: number;
  open_price: number;
  close_price: number | null;
  gross_pnl: number;
  status: 'OPEN' | 'CLOSED';
  opened_at: string;
  closed_at: string | null;
}

export interface CopySignal {
  id: string;
  provider?: CopyProvider;
  name: string;
  description: string | null;
  avatar_url: string | null;
  symbol_filter: string[] | null;
  direction_filter: 'all' | 'buy' | 'sell';
  default_lot_size: number | null;
  default_multiplier: number | null;
  default_max_positions: number;
  default_sl_pct: number | null;
  default_tp_pct: number | null;
  min_capital: number | null;
  reference_equity: number | null;
  account_type: 'STANDARD' | 'CENT' | 'MINI' | 'NANO';
  is_active: boolean;
  is_visible: boolean;
  is_verified: boolean;
  total_trades: number;
  winning_trades: number;
  losing_trades: number;
  total_pnl: number;
  win_rate: number;
  created_at: string;
}

export interface CopySubscription {
  id: string;
  signal: CopySignal | null;
  copierBot: {
    id: string;
    name: string;
    is_active: boolean;
    virtual_equity: number;
    assigned_capital: number;
    total_pnl: number;
    combined_pnl?: number;
    floating_pnl?: number;
    archived_at: string | null;
  } | null;
  allocation_mode: 'FIXED_LOT' | 'FIXED_MULT';
  fixed_lot: number | null;
  multiplier: number | null;
  max_positions: number;
  status: 'ACTIVE' | 'PAUSED' | 'STOPPED';
  paused_reason: string | null;
  total_profit: number;
  created_at: string;
}

// --- Signal Queries (public) ---

export function useCopySignals(opts?: { sortBy?: string }) {
  return useQuery<{ signals: CopySignal[]; total: number }>({
    queryKey: ['copy-signals', opts],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts?.sortBy) params.set('sortBy', opts.sortBy);
      const qs = params.toString();
      const res = await api.get(`/copy/signals${qs ? `?${qs}` : ''}`);
      return res.data;
    },
  });
}

export function useCopySignal(id: string) {
  return useQuery<CopySignal>({
    queryKey: ['copy-signals', id],
    queryFn: async () => {
      const res = await api.get(`/copy/signals/${id}`);
      return res.data;
    },
    enabled: !!id,
    refetchInterval: 10_000,
  });
}

export function useSignalTrades(signalId: string, opts?: { status?: string; limit?: number; page?: number; refetchInterval?: number }) {
  const { refetchInterval, ...queryOpts } = opts ?? {};
  return useQuery<{ trades: ProviderTrade[]; total: number }>({
    queryKey: ['copy-signals', signalId, 'trades', queryOpts],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (queryOpts?.status) params.set('status', queryOpts.status);
      if (queryOpts?.limit) params.set('limit', String(queryOpts.limit));
      if (queryOpts?.page) params.set('offset', String(((queryOpts.page || 1) - 1) * (queryOpts.limit || 50)));
      const qs = params.toString();
      const res = await api.get(`/copy/signals/${signalId}/trades${qs ? `?${qs}` : ''}`);
      return res.data;
    },
    enabled: !!signalId,
    refetchInterval,
  });
}

// --- Admin: Provider Signals ---

export function useProviderSignals(providerId: string) {
  return useQuery<CopySignal[]>({
    queryKey: ['admin', 'providers', providerId, 'signals'],
    queryFn: async () => {
      const res = await api.get(`/admin/copy/providers/${providerId}/signals`);
      return res.data;
    },
    enabled: !!providerId,
  });
}

export function useCreateSignal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { providerId: string; name: string; description?: string; symbol_filter?: string[]; direction_filter?: string; default_lot_size?: number; default_max_positions?: number; default_sl_pct?: number; default_tp_pct?: number; min_capital?: number; account_type?: 'STANDARD' | 'CENT' | 'MINI' | 'NANO' }) => {
      const res = await api.post('/admin/copy/signals', data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      qc.invalidateQueries({ queryKey: ['copy-signals'] });
      toast.success('Signal created');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to create signal');
    },
  });
}

export function useUpdateSignal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CopySignal> }) => {
      const res = await api.patch(`/admin/copy/signals/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      qc.invalidateQueries({ queryKey: ['copy-signals'] });
      toast.success('Signal updated');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to update signal');
    },
  });
}

export function useDeleteSignal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/admin/copy/signals/${id}`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      qc.invalidateQueries({ queryKey: ['copy-signals'] });
      toast.success('Signal deleted');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to delete signal');
    },
  });
}

// --- Analytics ---

export interface DailyPnl {
  date: string;
  pnl: number;
  cumulative: number;
}

export interface PeriodPerformance {
  pnl: number;
  trades: number;
  winRate: number;
  wins: number;
  losses: number;
}

export interface CopierStats {
  totalCopiers: number;
  activeCopiers: number;
  totalAssets: number;
}

export function useSignalDailyPnl(signalId: string, days = 30) {
  return useQuery<DailyPnl[]>({
    queryKey: ['copy-signals', signalId, 'daily-pnl', days],
    queryFn: async () => {
      const res = await api.get(`/copy/signals/${signalId}/daily-pnl?days=${days}`);
      return res.data;
    },
    enabled: !!signalId,
  });
}

export function useSignalPerformance(signalId: string) {
  return useQuery<Record<string, PeriodPerformance>>({
    queryKey: ['copy-signals', signalId, 'performance'],
    queryFn: async () => {
      const res = await api.get(`/copy/signals/${signalId}/performance`);
      return res.data;
    },
    enabled: !!signalId,
    refetchInterval: 30_000,
  });
}

export function useSignalCopierStats(signalId: string) {
  return useQuery<CopierStats>({
    queryKey: ['copy-signals', signalId, 'copier-stats'],
    queryFn: async () => {
      const res = await api.get(`/copy/signals/${signalId}/copier-stats`);
      return res.data;
    },
    enabled: !!signalId,
  });
}

// --- Bot Signal Info ---

export function useBotSignal(botId: string | undefined) {
  return useQuery<{ subscription: CopySubscription; signal: CopySignal } | null>({
    queryKey: ['copy-bot-signal', botId],
    queryFn: async () => {
      try {
        const res = await api.get(`/copy/bot/${botId}/signal`);
        return res.data || null;
      } catch {
        return null;
      }
    },
    enabled: !!botId,
  });
}

// --- Live Prices ---

/**
 * Real-time Vantage prices via Socket.IO (1/sec from bridge).
 * Falls back to REST polling if socket disconnects.
 */
export function useCopyPrices() {
  const [prices, setPrices] = useState<Record<string, { ask: number; bid: number }>>({});

  useEffect(() => {
    const socket = getSocket();

    socket.emit('subscribe:copy-prices');

    const handler = (data: Record<string, { ask: number; bid: number }>) => {
      setPrices(data);
    };

    socket.on('copy:prices', handler);

    // Fallback: fetch initial prices via REST
    api.get('/copy/prices').then(res => {
      if (res.data && Object.keys(res.data).length > 0) {
        setPrices(res.data);
      }
    }).catch(() => {});

    return () => {
      socket.emit('unsubscribe:copy-prices');
      socket.off('copy:prices', handler);
    };
  }, []);

  return { data: prices };
}

// --- Subscription Queries ---

export function useCopySubscriptions() {
  return useQuery<CopySubscription[]>({
    queryKey: ['copy-subscriptions'],
    queryFn: async () => {
      const res = await api.get('/copy/subscriptions');
      return res.data;
    },
  });
}

// --- Mutations ---

export function useSubscribeToCopy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      signalId: string;
      mt5AccountId: string;
      allocationMode: 'FIXED_LOT' | 'FIXED_MULT' | 'EQUITY_RATIO';
      fixedLot?: number;
      multiplier?: number;
      maxPositions?: number;
      minLotAction?: 'OPEN_MIN' | 'SKIP';
      maxLot?: number;
      assignedCapital: number;
      slPct?: number;
      tpPct?: number | null;
    }) => {
      const res = await api.post('/copy/subscribe', data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['copy-subscriptions'] });
      qc.invalidateQueries({ queryKey: ['bots'] });
      toast.success('Subscribed to signal');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to subscribe');
    },
  });
}

export function useUnsubscribeFromCopy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (subscriptionId: string) => {
      const res = await api.delete(`/copy/subscriptions/${subscriptionId}`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['copy-subscriptions'] });
      qc.invalidateQueries({ queryKey: ['bots'] });
      toast.success('Unsubscribed');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to unsubscribe');
    },
  });
}

// --- Admin: Vantage Accounts ---

export interface VantageAccount {
  id: string;
  name: string;
  account_id: string;
  server_account_id: string;
  email: string;
  is_active: boolean;
  last_login_at: string | null;
  last_error: string | null;
  created_at: string;
}

export function useVantageAccounts() {
  return useQuery<VantageAccount[]>({
    queryKey: ['admin', 'vantage-accounts'],
    queryFn: async () => {
      const res = await api.get('/admin/copy/vantage-accounts');
      return res.data;
    },
  });
}

export function useCreateVantageAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<VantageAccount> & { password?: string; sign_secret?: string }) => {
      const res = await api.post('/admin/copy/vantage-accounts', data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'vantage-accounts'] });
      toast.success('Vantage account created');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to create Vantage account');
    },
  });
}

export function useProvider(providerId: string) {
  return useQuery<CopyProvider & { vantageAccount?: { name: string; account_id: string } | null }>({
    queryKey: ['admin', 'providers', providerId],
    queryFn: async () => {
      const res = await api.get(`/admin/copy/providers/${providerId}`);
      return res.data;
    },
    enabled: !!providerId,
  });
}

export function useProviderTrades(providerId: string, opts?: { status?: string; limit?: number; page?: number }) {
  return useQuery<{ trades: ProviderTrade[]; total: number }>({
    queryKey: ['admin', 'providers', providerId, 'trades', opts],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts?.status) params.set('status', opts.status);
      if (opts?.limit) params.set('limit', String(opts.limit));
      if (opts?.page && opts.page > 1) params.set('offset', String((opts.page - 1) * (opts?.limit || 20)));
      const qs = params.toString();
      const res = await api.get(`/admin/copy/providers/${providerId}/trades${qs ? `?${qs}` : ''}`);
      return res.data;
    },
    enabled: !!providerId,
    refetchInterval: 10_000,
  });
}

export function useVantageAccountProviders(accountId: string) {
  return useQuery<CopyProvider[]>({
    queryKey: ['admin', 'vantage-accounts', accountId, 'providers'],
    queryFn: async () => {
      const res = await api.get(`/admin/copy/vantage-accounts/${accountId}/providers`);
      return res.data;
    },
    enabled: !!accountId,
  });
}

export function useUpdateProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CopyProvider> }) => {
      const res = await api.patch(`/admin/copy/providers/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      qc.invalidateQueries({ queryKey: ['copy-providers'] });
      toast.success('Provider updated');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to update provider');
    },
  });
}

// --- Bridge Control ---

export interface BridgeStatus {
  running: boolean;
  accountId: string | null;
  accountName: string | null;
  providersCount: number;
  lastSync: string | null;
  pid: number | null;
  processAlive?: boolean;
}

export function useBridgeStatus() {
  return useQuery<BridgeStatus>({
    queryKey: ['admin', 'bridge-status'],
    queryFn: async () => {
      const res = await api.get('/admin/copy/bridge/status');
      return res.data;
    },
    refetchInterval: 5000,
  });
}

export function useStartBridge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post('/admin/copy/bridge/start');
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'bridge-status'] });
      qc.invalidateQueries({ queryKey: ['admin', 'vantage-accounts'] });
      toast.success('Bridge started');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to start bridge');
    },
  });
}

export function useStopBridge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post('/admin/copy/bridge/stop');
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'bridge-status'] });
      toast.success('Bridge stopped');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to stop bridge');
    },
  });
}

export function useDeleteVantageAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/admin/copy/vantage-accounts/${id}`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'vantage-accounts'] });
      toast.success('Vantage account deleted');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to delete');
    },
  });
}
